import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Bluebird from 'bluebird';
import { Box, Button, Breadcrumbs, Typography, MobileStepper } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { generatePath } from 'react-router-dom';
import { fetchRescueSizesIfNeeded } from '../../../../actions/rescueSizes';
import {
  fetchSiteDonorsLocationsIfNeeded,
  fetchSitePickupLocationsIfNeeded,
  fetchSiteReceiversIfNeeded,
  fetchSiteRescuersIfNeeded,
} from '../../../../actions/sites';
import useActiveSite from '../../../../hooks/useActiveSite';
import {
  createDraftDonation,
  diffDonation,
  DONATION_TYPE_REQUEST,
  setDonationDiffInflight,
} from '../../../../actions/donationNew';
import DonationContext from '../../../../context/DonationContext/DonationContext';
import useDonationManageActions from '../../../../hooks/useDonationManageActions';
import { donationManageActions } from '../../../../helpers/donations';
import routes from '../../../../routes';
import {
  FREQUENCY_EVERY_OTHER_WEEK,
  FREQUENCY_ONCE,
  FREQUENCY_WEEKLY_EVERY_TWO_WEEKS,
  FREQUENCY_WEEKLY_EVERY_WEEK,
} from '../../../../models/donationsNew';
import { fetchSystemSettingsIfNeeded } from '../../../../actions/systemSettings';
import errorMessages from '../../../../assets/errorMessages';
import { validateDonation } from '../donationValidator';
import useActiveUser from '../../../../hooks/useActiveUser';
import * as authService from '../../../../services/auth';
import { Roles } from '../../../../models/roles';
import { saveRequestDonation } from '../../../../actions/requestDonations';
import RequestToDonateDonationStep from '../Components/RequestToDonateDonationStep';
import RequestToDonateLocationAndTimeStep from '../Components/RequestToDonateLocationAndTimeStep';
import RequestToDonateNotesStep from '../Components/RequestToDonateNotesStep';
import snackbarHelper from '../../../../helpers/snackbarHelper';

const stepKeys = ['donation', 'locationAndTime', 'notes'];

const steps = stepKeys.filter(Boolean).reduce((acc, key, index) => {
  acc[key] = index;
  return acc;
}, {});

const DonationRequestCreateMobile = ({ isMobileView, history, location }) => {
  const dispatch = useDispatch();
  const activeSite = useActiveSite();
  const activeUser = useActiveUser();
  const [firstFoodDonorChoice, setFirstFoodDonorChoice] = useState();
  const isUIDisabled = useSelector((state) => state.ui.uiDisabled);
  const rescueSizes = useSelector((state) => state.entities.rescueSizes);
  const pickupLocations = useSelector((state) => state.entities.sites.pickupLocations);
  const foodDonors = useSelector((state) => state.entities.sites.donorsLocations);
  const donationData = useSelector((state) => state.ui.donation_new.draft);
  const currentDonationState = useSelector((state) => state.ui.donation_new.currentState);
  const diffInflight = useSelector((state) => state.ui.donation_new.diffInflight);
  const donationFormErrors = useSelector((state) => state.ui.donation_new.errors);
  const [activeStep, setActiveStep] = useState(0);

  const hasFoodDonorDefined = donationData && !!donationData.location;

  const handleFoodDonorChange = (foodDonorId) => {
    if (firstFoodDonorChoice) {
      return;
    }

    setFirstFoodDonorChoice(foodDonorId);
  };

  const {
    clearDonation,
    undoLastAction,

    setError,
    removeError,
    clearErrors,

    setFoodDonor,
    setPickupLocation,
    setDescription,
    setLBSValue,
    setAddFoodTypes,
    setRemoveFoodTypes,
    setFoodTypeOther,
    setRescueSize,
    setFoodSize,
    setAddDayOfWeek,
    setRemoveDayOfWeek,

    setDonorNotes,

    setAddDayOfMonth,
    setDayOfMonthDay,
    setDayOfMonthOrdinal,
    setRemoveDayOfMonth,

    setStartDate,
    setEndDate,
    setPauseDate,
    setResumeDate,
    setFrequency,
    setAppendPickup,
    setRemovePickup,
    setPickupReceiver,
    setPickupAdopter,
    setPickupRescuerNotes,
    setPickupBegin,
    setPickupEnd,
    setAllDaysTheSame,
    clearAllDaysTheSame,
    setAllPickupsTheSame,
    clearAllPickupsTheSame,

    setRescuePickupBegin,
    setRescuePickupEnd,
    setRescueRescuerNotes,
    setRescueCanceller,
    setRescueRescuer,
  } = useDonationManageActions();

  useEffect(() => {
    dispatch(
      createDraftDonation({
        donation: {
          food_donor_notes: '',
          description: '',
          lbs: null,
          start_date: {
            value: moment().add(1, 'day').format('YYYY-MM-DD'),
            format: 'YYYY-MM-DD',
          },
          end_date: null,
          pause_date: null,
          resume_date: null,
          frequency: FREQUENCY_ONCE,
          frequency_every: null,
          days_of_week: {
            mon: {
              checked: false,
              allow_uncheck: null,
            },
            tue: {
              checked: false,
              allow_uncheck: null,
            },
            wed: {
              checked: false,
              allow_uncheck: null,
            },
            thu: {
              checked: false,
              allow_uncheck: null,
            },
            fri: {
              checked: false,
              allow_uncheck: null,
            },
            sat: {
              checked: false,
              allow_uncheck: null,
            },
            sun: {
              checked: false,
              allow_uncheck: null,
            },
          },
          days_of_month: {
            mon: [],
            tue: [],
            wed: [],
            thu: [],
            fri: [],
            sat: [],
            sun: [],
          },
          all_days_are_the_same: true,
          all_pickups_are_the_same: true,
          food_types: ['fresh'],
          food_type_other: null,
          rescue_size: null,
          location: null,
          pickup_location: null,
        },
      })
    );
    dispatch(fetchSiteReceiversIfNeeded(activeSite.id));
    dispatch(fetchSiteDonorsLocationsIfNeeded(activeSite.id));
    dispatch(fetchSitePickupLocationsIfNeeded(activeSite.id));
    dispatch(fetchRescueSizesIfNeeded());
    dispatch(fetchSiteRescuersIfNeeded(activeSite.id));
    dispatch(fetchSystemSettingsIfNeeded()); // used in LBSSelector
    if (location.state && location.state.copy && location.state.rescue) {
      handleFoodDonorChange(location.state.rescue.location_id);
      setFoodDonor({
        id: location.state.rescue.location_id,
        name: location.state.rescue.location,
      });
      setPickupLocation({
        name: location.state.rescue.pickup_location_name,
        id: location.state.rescue.pickup_location_id,
      });
      setRescueSize({ id: location.state.rescue.rescue_size_id });
      location.state.rescue.food_types.split(',').forEach((foodType) => setAddFoodTypes(foodType));
      location.state.rescue.food_type_other && setFoodTypeOther(location.state.rescue.food_type_other);
      location.state.rescue.slug && setDescription(location.state.rescue.slug);
      setPickupRescuerNotes({
        rescuer_notes: location.state.rescue.rescuer_notes,
      });

      // clear location.state
      history.replace({
        ...location,
        state: {
          ...location.state,
          copy: false,
          rescue: undefined,
        },
      });
    }

    return () => clearDonation();
  }, [dispatch]);

  const onSubmit = async () => {
    try {
      const validation = validateDonation(donationData, {
        lbsRecording: false,
        validate24HoursAheadOfTimeWindow: true,
      });

      clearErrors();
      if (!validation.isValid) {
        snackbarHelper.error('Form not valid. Please go back and confirm all required fields are filled correctly.');
        return Object.keys(validation.errors).forEach((error) => setError(error, validation.errors[error]));
      }
      await dispatch(saveRequestDonation(activeSite, activeUser, donationData));
      snackbarHelper.success(
        'Thank you for submitting your food donation request! You will be notified when your donation request is confirmed'
      );
      history.push(generatePath(routes.mobileDashboardFoodDonorRequests));
    } catch (e) {
      dispatch(setDonationDiffInflight(false));

      return snackbarHelper.error(`Unable to create donation: ${e.message}`);
    }
  };

  const triggerFormChange = () => {
    if (firstFoodDonorChoice) {
      return Bluebird.try(() => dispatch(diffDonation(null, firstFoodDonorChoice))).catch((e) => {
        snackbarHelper.error(e?.message || 'Unknown error during modifying donation');
        dispatch(setDonationDiffInflight(false));
        undoLastAction();
      });
    }
  };

  const handleFieldChange = (actionName, fieldValue) => {
    switch (actionName) {
      case donationManageActions.set_food_donor:
        if (fieldValue) {
          handleFoodDonorChange(fieldValue.id);
          setFoodDonor(fieldValue);
          triggerFormChange();
        }
        break;

      case donationManageActions.set_pickup_location:
        setPickupLocation(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_description:
        setDescription(fieldValue);
        break;

      case donationManageActions.set_lbs_value:
        setLBSValue(fieldValue);
        if (fieldValue === '') {
          setError('lbs', errorMessages.REQUIRED.message);
        } else {
          removeError('lbs');
        }
        triggerFormChange();
        break;

      case donationManageActions.set_rescue_size:
        setRescueSize(fieldValue);
        break;

      case donationManageActions.set_food_size:
        setFoodSize(fieldValue);
        break;

      case donationManageActions.add_food_type:
        setAddFoodTypes(fieldValue);
        break;

      case donationManageActions.remove_food_type:
        setRemoveFoodTypes(fieldValue);
        break;

      case donationManageActions.set_food_type_other:
        setFoodTypeOther(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_frequency:
        const frequencyEveryValue = (() => {
          if (fieldValue === FREQUENCY_EVERY_OTHER_WEEK) {
            return FREQUENCY_WEEKLY_EVERY_TWO_WEEKS;
          }

          return FREQUENCY_WEEKLY_EVERY_WEEK;
        })();

        setFrequency(fieldValue);
        setStartDate({
          startDate: moment().add(1, 'day').format('YYYY-MM-DD'),
          frequency: fieldValue === FREQUENCY_EVERY_OTHER_WEEK ? FREQUENCY_WEEKLY_EVERY_WEEK : fieldValue,
          frequencyEvery: frequencyEveryValue,
        });
        triggerFormChange();
        break;

      case donationManageActions.add_day_of_week:
        setAddDayOfWeek(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.remove_day_of_week:
        setRemoveDayOfWeek(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.add_day_of_month:
        setAddDayOfMonth(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_day_of_month_day:
        setDayOfMonthDay(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_day_of_month_ordinal:
        setDayOfMonthOrdinal(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.remove_day_of_month:
        setRemoveDayOfMonth(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_start_date:
        removeError('start_date');
        setStartDate({ startDate: fieldValue });
        triggerFormChange();
        break;

      case donationManageActions.set_end_date:
        setEndDate(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_pause_date:
        setPauseDate(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_resume_date:
        setResumeDate(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_all_days_are_the_same:
        setAllDaysTheSame();
        triggerFormChange();
        break;

      case donationManageActions.clear_all_days_are_the_same:
        clearAllDaysTheSame();
        triggerFormChange();

        break;
      case donationManageActions.set_all_pickups_are_the_same:
        setAllPickupsTheSame();
        triggerFormChange();
        break;

      case donationManageActions.clear_all_pickups_are_the_same:
        clearAllPickupsTheSame();
        triggerFormChange();
        break;

      case donationManageActions.add_pickup:
        setAppendPickup(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.remove_pickup:
        setRemovePickup(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_pickup_receiver:
        setPickupReceiver(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_pickup_adopter:
        setPickupAdopter(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_pickup_rescuer_notes:
        setPickupRescuerNotes(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_pickup_begin:
        setPickupBegin(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_pickup_end:
        setPickupEnd(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_rescue_pickup_begin:
        setRescuePickupBegin(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_rescue_pickup_end:
        setRescuePickupEnd(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_rescue_rescuer_notes:
        setRescueRescuerNotes(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_rescue_canceller:
        setRescueCanceller(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_rescue_rescuer:
        setRescueRescuer(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_donor_notes:
        setDonorNotes(fieldValue);
        triggerFormChange();
        break;

      default:
        console.error(`---->>>> #Unknown donation field "${actionName}" with given value "${fieldValue}"`);
    }
  };

  useEffect(() => {
    const foodDonorsLength = Object.keys(foodDonors.byId).length;
    if (foodDonorsLength && !firstFoodDonorChoice) {
      const userRoles = authService.getUserRolesInCurrentlySelectedSite(activeUser);
      const rolesWithAccess = userRoles.filter(
        (role) =>
          [Roles.DonorLocationAdmin, Roles.DonorAdmin, Roles.DonorLocationStaff, Roles.DonorStaff].indexOf(
            role.role_name
          ) > -1
      );

      if (rolesWithAccess.length) {
        // Check if the request is not a copy of a rescue
        if (!(location.state && location.state.copy && location.state.rescue)) {
          setFirstFoodDonorChoice(rolesWithAccess[0].donor_location_id);
          if (rescueSizes?.sorted?.length > 0) {
            setRescueSize({ id: rescueSizes.sorted[0]?.id });
          }
        }
        setStartDate({
          startDate: moment().add(1, 'day').format('YYYY-MM-DD'),
        });
      } else {
        history.push(routes.index);
      }
    }
  }, [activeUser.id, foodDonors.inflight, rescueSizes.inflight, Object.keys(foodDonors.byId).length]);

  useEffect(() => {
    if (firstFoodDonorChoice) {
      handleFieldChange(donationManageActions.set_food_donor, foodDonors.byId[firstFoodDonorChoice]);
    }
  }, [firstFoodDonorChoice]);

  if (
    !firstFoodDonorChoice ||
    donationData === null ||
    rescueSizes.inflight ||
    foodDonors.inflight ||
    pickupLocations.inflight
  ) {
    return 'loading...';
  }

  const handleNext = () => {
    const validation = validateDonation(donationData, {
      lbsRecording: false,
      validate24HoursAheadOfTimeWindow: true,
    });

    clearErrors();

    if (activeStep === steps.donation && validation?.errors?.lbs?.length) {
      return Object.keys(validation.errors).forEach((error) => setError(error, validation.errors[error]));
    }

    if (activeStep !== steps.donation && !validation.isValid) {
      return Object.keys(validation.errors).forEach((error) => setError(error, validation.errors[error]));
    }

    return setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    if (activeStep === 0) {
      return;
    }

    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const renderNextButton = () => {
    if (activeStep === steps.notes) {
      return (
        <Button
          disabled={isUIDisabled || currentDonationState.length === 0}
          color="primary"
          variant="contained"
          type="button"
          size="small"
          onClick={onSubmit}
          data-testid="submit-donation-request"
        >
          Create
        </Button>
      );
    }

    return (
      <Button
        disabled={isUIDisabled}
        data-introid="donation-request-next"
        data-testid="donation-request-next"
        size="small"
        onClick={handleNext}
      >
        Next
        <KeyboardArrowRight />
      </Button>
    );
  };

  return (
    <DonationContext.Provider
      value={{
        onFieldChange: handleFieldChange,
        isLoading: diffInflight,
        hasFoodDonorDefined: hasFoodDonorDefined,
        showFoodDonorSelector: false,
        isMobileView: isMobileView,
        isNew: true,
        errors: donationFormErrors,
        donationType: DONATION_TYPE_REQUEST,
        isMobileNative: true,
      }}
    >
      <Breadcrumbs aria-label="Breadcrumbs">
        {activeStep === steps.donation && <Typography color="textPrimary">What would you like to donate?</Typography>}
        {activeStep === steps.locationAndTime && (
          <Typography color="textPrimary">Where and when should we pick up your donation?</Typography>
        )}
        {activeStep === steps.notes && <Typography color="textPrimary">Anything else we should know?</Typography>}
      </Breadcrumbs>

      <Box boxShadow={25} py={2} mb={2} px={2} maxWidth="720px" bgcolor="background.paper" borderRadius="16px">
        {activeStep === steps.donation && <RequestToDonateDonationStep />}
        {activeStep === steps.locationAndTime && <RequestToDonateLocationAndTimeStep />}
        {activeStep === steps.notes && <RequestToDonateNotesStep />}

        <Box pt={2}>
          <MobileStepper
            variant="progress"
            steps={Object.values(steps).length}
            position="static"
            LinearProgressProps={{
              style: {
                width: '100%',
              },
            }}
            activeStep={activeStep}
            style={{ backgroundColor: 'white' }}
            nextButton={renderNextButton()}
            backButton={
              <Button
                data-introid="donation-request-back"
                data-testid="donation-request-back"
                size="small"
                onClick={handleBack}
                disabled={activeStep === 0 || isUIDisabled}
              >
                <KeyboardArrowLeft />
                Back
              </Button>
            }
          />
        </Box>
      </Box>
    </DonationContext.Provider>
  );
};

export default DonationRequestCreateMobile;
